import type {DalValue, ComponentWithResolvedDataItems, SchemaService, VariantSplit} from '@wix/document-services-types'
import {VARIANTS_DATA_TYPES} from '@wix/format-converter-schemas'
import _ from 'lodash'
import {REF_ARRAY_DATA_TYPE} from '../../constants'
import type {SingleComponentPluginFunction} from '../../types'
import {
    type ReferredDataItemsIterateeContext,
    type VariantRelationPossiblySerialized,
    forEachReferredDataItem
} from '@wix/import-export-utils'
import {traverseInnerElementsForVariants} from './traverse'

const VARIANT_RELATION_DATA_TYPE = 'VariantRelation'

/**
 * Check if a value is a RefArray structure
 */
const isRefArray = (value: unknown): value is {type: string; values: unknown[]} => {
    return _.isPlainObject(value) && (value as Record<string, unknown>).type === REF_ARRAY_DATA_TYPE
}

/**
 * Convert RefArray to VariantSplit format
 * RefArray: { type: 'RefArray', values: [default, ...VariantRelations] }
 * VariantSplit: { default: value, variants: [{value, conditions}] }
 */
export const convertDataItemToVariantSplit = (item: DalValue): VariantSplit => {
    if (item.type !== REF_ARRAY_DATA_TYPE) {
        return {default: item}
    }

    const [defaultValues, variants] = _.partition(item.values, ref => ref.type !== VARIANT_RELATION_DATA_TYPE)

    const variantSplit: VariantSplit = {
        variants: variants.map((variant: VariantRelationPossiblySerialized) => ({
            value: variant.to,
            conditions: variant.variants
        }))
    }

    const defaultValue = defaultValues[0]
    if (defaultValue) {
        variantSplit.default = defaultValues[0]
    }

    return variantSplit
}

/**
 * Process variant-supporting namespaces on an innerElement
 * Converts RefArray structures to VariantSplit format
 */
const packInnerElementVariants = (element: Record<string, unknown>): void => {
    _.forOwn(VARIANTS_DATA_TYPES, (namespace: string) => {
        const featureData = element[namespace]
        if (!featureData) {
            return
        }

        // Direct RefArray on the namespace (e.g., element.style = RefArray)
        if (isRefArray(featureData)) {
            element[namespace] = convertDataItemToVariantSplit(featureData as DalValue)
        }
    })
}

/**
 * Pack function that converts RefArray to VariantSplit
 * Processes both component-level data and innerElements
 */
export const pack: SingleComponentPluginFunction = (
    component: ComponentWithResolvedDataItems,
    schemaService: SchemaService
): void => {
    // Process component-level variant namespaces
    _.forOwn(VARIANTS_DATA_TYPES, (namespace: string) => {
        const componentData = component.data
        const featureData = componentData[namespace]
        if (!featureData) {
            return
        }

        forEachReferredDataItem(
            schemaService,
            featureData,
            namespace,
            ({item, itemRefInfo, ownerItem = componentData}: ReferredDataItemsIterateeContext) => {
                if (itemRefInfo.isRelationalSplit) {
                    _.set(ownerItem, itemRefInfo.path, convertDataItemToVariantSplit(item))
                }
            }
        )
    })

    // Process innerElements variant namespaces
    traverseInnerElementsForVariants(component, ({element}) => {
        packInnerElementVariants(element)
    })
}
